import { useState } from "react";
import { Users, Share2, Check, X, Volume2, VolumeX, Eye, Crown, Heart, BadgeCheck } from "lucide-react";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { isSoundEnabled, setSoundEnabled } from "@/lib/giftEffects";
import UserBadge from "@/components/UserBadge";

export default function LiveRoomTopBar({
  stream,
  role,
  viewerCount,
  giftCount,
  heartsTotal,
  copied,
  onToggleViewers,
  onShare,
  onClose,
  isHost,
  onEndStream,
  topGifters = [],
  headerRef,
  children,
}) {
  const [soundOn, setSoundOn] = useState(() => isSoundEnabled());
  const toggleSound = () => {
    const next = !soundOn;
    setSoundEnabled(next);
    setSoundOn(next);
  };
  // Iter60 — host X requires an explicit confirmation. Without this the host
  // could tap X, close the client tab and leave the LiveKit session running
  // invisibly (Iter58 Egress leak also related). Viewers/guests keep the
  // existing lightweight onClose behaviour (just unmounts the room UI).
  const [confirmEnd, setConfirmEnd] = useState(false);
  const handleClose = () => {
    if (isHost) setConfirmEnd(true);
    else onClose?.();
  };
  const handleConfirmEnd = async () => {
    setConfirmEnd(false);
    // Iter64 fix — endStream() is the sole authority for closing a host
    // Live. It calls onClose on success and on force-close-after-3-attempts;
    // on transient failure it surfaces the red "attempt N/3" toast and
    // leaves the room mounted so the host can retry without losing the
    // session. Previously this handler had `onClose?.()` in a `finally`
    // block which fired regardless of success/failure — closing the UI
    // while the backend still had is_live=true, effectively giving hosts
    // a silent "orphaned live" bug.
    if (onEndStream) {
      await onEndStream();
    } else {
      // Non-host X (viewer leaving live) still needs the plain close path.
      onClose?.();
    }
  };
  const top3 = (topGifters || []).slice(0, 3);
  return (
    <div
      ref={headerRef}
      data-testid="live-header"
      className="absolute left-2 right-2 z-30 flex flex-col gap-1.5 pointer-events-none"
      style={{ top: "max(0.5rem, calc(env(safe-area-inset-top) + 0.25rem))" }}
    >
    <div className="flex items-start justify-between gap-1.5">
      {/* Part 6 — ONE compact safe-area chip: host · LIVE · 👁 viewers · 🎋 gifts.
          Title moved to the tooltip so the chip never grows over a face. */}
      {/* Mobile-readable header: row 1 = host chip (avatar · name · LIVE); row 2 = stats pill + top gifters.
          Nothing is crammed into the 9px sub-line any more; every control keeps its test id and behaviour. */}
      <div className="pointer-events-auto flex flex-col items-start gap-1 min-w-0" data-testid="live-header-left">
      <UserBadge
        userRef={stream.host?.id || stream.host?.handle}
        data-testid="live-host-chip-peek"
        aria-label={`View ${stream.host?.display_name}'s profile`}
        title={stream.title || undefined}
        className="flex items-center gap-2 bg-black/55 backdrop-blur-md border border-white/10 rounded-full pr-2.5 pl-0.5 py-0.5 hover:border-rose-500/60 transition-colors text-left min-w-0 max-w-[62vw] sm:max-w-[340px]"
      >
        <Avatar className="w-7 h-7 border border-white/20 shrink-0">
          <AvatarImage src={stream.host?.avatar_url} />
          <AvatarFallback className="bg-rose-600 text-white text-[10px]">
            {stream.host?.display_name?.[0]?.toUpperCase() || "?"}
          </AvatarFallback>
        </Avatar>
        <span className="text-white text-[13px] font-semibold leading-none truncate min-w-0">
          {stream.host?.handle_hidden ? (stream.host?.display_name || "Host") : `${stream.host?.display_name}`}
        </span>
        {stream.host?.is_verified && <BadgeCheck size={14} className="fill-sky-500 text-white shrink-0" aria-label="Verified" data-testid="live-host-chip-verified" />}
        <span className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-rose-300 leading-none shrink-0 pl-1 border-l border-white/15">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 pulse-dot" />Live
        </span>
      </UserBadge>
      <div className="flex items-center gap-1.5">
        <div data-testid="live-header-stats" className="flex items-center h-7 gap-2.5 bg-black/55 backdrop-blur-md border border-white/10 rounded-full px-2.5 text-[11px] font-medium tabular-nums leading-none">
          <span data-testid="live-status-viewers" className="flex items-center gap-1 text-zinc-100" title="Watching now"><Eye size={12} className="text-emerald-400" /><span data-testid="live-viewer-count">{viewerCount ?? 0}</span></span>
          <span className="w-px h-3 bg-white/15" aria-hidden="true" />
          <span data-testid="live-status-gifts" className="flex items-center gap-1 text-rose-100" title="Bamboo gifts this Live"><span data-testid="live-gift-icon" className="text-[12px] leading-none" aria-label="Bamboo">🎋</span><span data-testid="live-gift-count">{giftCount ?? 0}</span></span>
          <span className="w-px h-3 bg-white/15" aria-hidden="true" />
          <span data-testid="live-status-hearts" className="flex items-center gap-1 text-pink-100" title="Hearts this Live"><Heart size={12} className="text-pink-400 fill-pink-400" /><span data-testid="live-hearts-count">{Number(heartsTotal ?? 0).toLocaleString()}</span></span>
        </div>
        {/* Top Gifters — 3 stacked avatars, tap any to open the profile. */}
        <div
          data-testid="live-top-gifters"
          className="flex items-center h-7 bg-black/55 backdrop-blur-md border border-white/10 rounded-full pl-2 pr-2 shrink-0"
          title="Top gifters"
        >
          <Crown size={11} className="text-amber-300 mr-1 shrink-0" />
          {top3.length === 0 ? (
            <span data-testid="live-top-gifters-empty" className="text-[10px] text-zinc-400 whitespace-nowrap">No gifts yet</span>
          ) : (
            <div className="flex items-center -space-x-1.5">
              {top3.map((g, i) => (
                <UserBadge key={g.id || i} userRef={g.id} data-testid={`live-top-gifter-${i + 1}`} aria-label={`View ${g.display_name}'s profile`} className="hover:opacity-80 transition-opacity rounded-full">
                  <Avatar className={`w-5 h-5 border ${i === 0 ? "border-amber-300" : i === 1 ? "border-zinc-300" : "border-orange-400"}`}>
                    <AvatarImage src={g.avatar_url} />
                    <AvatarFallback className="bg-rose-600 text-white text-[8px]">{g.display_name?.[0]?.toUpperCase() || "?"}</AvatarFallback>
                  </Avatar>
                </UserBadge>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>

      <div className="pointer-events-auto flex items-center gap-1.5 shrink-0">
        {/* Slice A — Viewers count, Battle entry, Share, and speaker/mute
            (gift-sound) toggle are HIDDEN for the host to keep the creator
            top bar minimal: [chip] ... [Mic] [X]. Battle stays reachable
            via the existing Creator Tools wrench menu. Viewers count moves
            to the wrench in Slice B (do not add here). Share and the
            speaker/mute toggle are creator-irrelevant (they can't hear
            their own gift SFX and don't need to share their own stream
            from within their own view). Viewer top bar is intentionally
            untouched in Slice A. */}
        {/* Slice C — viewer controls kept (sound + share matter to viewers)
            but compacted: 32px targets, tighter pill, smaller icons. Host
            chrome untouched. */}
        {!isHost && (
          <>
            <button
              type="button"
              data-testid="live-viewers-toggle"
              onClick={onToggleViewers}
              className="flex items-center gap-1 bg-black/45 backdrop-blur-md border border-white/10 rounded-full px-2 py-1 text-white text-xs hover:border-white/25 transition-colors h-8"
            >
              <Users size={12} />
              {viewerCount}
            </button>
            <button
              type="button"
              data-testid="live-share-button"
              onClick={onShare}
              className="w-8 h-8 rounded-full bg-black/45 backdrop-blur-md border border-white/10 text-white flex items-center justify-center hover:border-white/25 transition-colors"
              title="Share"
            >
              {copied ? <Check size={14} /> : <Share2 size={14} />}
            </button>
            <button
              type="button"
              data-testid="live-sound-toggle"
              onClick={toggleSound}
              className={`w-8 h-8 rounded-full backdrop-blur-md border flex items-center justify-center transition-colors ${
                soundOn
                  ? "bg-black/45 border-white/10 text-white hover:border-yellow-300"
                  : "bg-black/80 border-white/20 text-zinc-400"
              }`}
              title={soundOn ? "Mute gift sounds" : "Unmute gift sounds"}
              aria-pressed={!soundOn}
            >
              {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </button>
          </>
        )}
        {/* Slice A — Host mic slot. HostTopBarMic renders into this node
            via React Portal from inside <LiveKitRoom>. Positioning here
            keeps Mic + X visually adjacent for the host without touching
            the LiveKit provider tree. */}
        {isHost && (
          <span
            id="live-host-mic-slot"
            data-testid="live-host-mic-slot"
            className="flex items-center"
          />
        )}
        <button
          type="button"
          data-testid="live-close-button"
          onClick={handleClose}
          className={`${isHost ? "w-9 h-9" : "w-8 h-8"} rounded-full bg-black/45 backdrop-blur-md border border-white/10 text-white flex items-center justify-center hover:border-white/25 transition-colors`}
          aria-label={isHost ? "End Live" : "Leave live"}
        >
          <X size={isHost ? 18 : 16} />
        </button>
      </div>
    </div>
    {children ? <div data-testid="live-header-row2" className="flex items-center gap-1.5 pointer-events-auto">{children}</div> : null}
      <AlertDialog open={confirmEnd} onOpenChange={setConfirmEnd}>        <AlertDialogContent
          data-testid="end-live-confirm"
          className="bg-zinc-950 border border-white/10 text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">End Live?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will end your broadcast for everyone watching. Your co-hosts and any active Battle will be finalized. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="end-live-cancel"
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              Keep Streaming
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="end-live-confirm-btn"
              onClick={handleConfirmEnd}
              className="bg-rose-600 hover:bg-rose-500 text-white"
            >
              End Live
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
